// ============================================================
// FILE: components/ApiUsagePanel.jsx
// Place in src/taskpane/components/
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import MonitoredCompaniesSection from './MonitoredCompaniesSection';

const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001' 
  : '';

const GaugeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const formatNumber = (n) => n?.toLocaleString() ?? '0';

const MonthPicker = ({ value, onChange }) => (
  <input
    type="month"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-cyan-500"
  />
);

// Simple bar chart component
const BarChart = ({ data, maxValue, labelKey, valueKey, color = 'cyan' }) => {
  if (!data || data.length === 0) return <p className="text-xs text-gray-500 italic">No data for this period</p>;
  
  const max = maxValue || Math.max(...data.map(d => d[valueKey]), 1);
  const colorMap = {
    cyan: { bar: 'bg-cyan-500', text: 'text-cyan-400' },
    yellow: { bar: 'bg-yellow-500', text: 'text-yellow-400' },
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-400' },
    purple: { bar: 'bg-purple-500', text: 'text-purple-400' },
  };
  const colors = colorMap[color] || colorMap.cyan;

  return (
    <div className="space-y-1">
      {data.map((item, i) => {
        const pct = (item[valueKey] / max) * 100;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="text-gray-400 w-16 text-right shrink-0 font-mono">
              {item[labelKey]?.slice(-5) || item[labelKey]}
            </span>
            <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
              <div 
                className={`${colors.bar} h-full rounded-full transition-all duration-300`}
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
            </div>
            <span className={`${colors.text} w-14 text-right shrink-0 font-mono`}>
              {formatNumber(item[valueKey])}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ApiUsagePanel = ({ config }) => {
  const [usageData, setUsageData] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('daily'); // 'daily' | 'functions' | 'history'
  const [responseUserId, setResponseUserId] = useState(''); // Set only by real API responses
  const [responseCompanyId, setResponseCompanyId] = useState(''); // Set only by real API responses

  const isExtUser = responseUserId.startsWith('ExtUser');
  const isAuthenticated = !!responseUserId; // True only after a real API call succeeds

  // Listen for auth updates broadcast from API responses
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.userId) setResponseUserId(e.detail.userId);
      if (e.detail?.companyId) {
        setResponseCompanyId(e.detail.companyId);
        // Auto-select the authenticated company if none selected yet, or if non-ExtUser
        if (!e.detail.userId?.startsWith('ExtUser')) {
          setSelectedCompany(e.detail.companyId);
        } else if (!selectedCompany) {
          setSelectedCompany(e.detail.companyId);
        }
      }
    };
    window.addEventListener('intacct-auth-userid', handler);
    return () => window.removeEventListener('intacct-auth-userid', handler);
  }, [selectedCompany]);

  // Load company list when auth changes
  useEffect(() => {
    if (isAuthenticated) fetchCompanies();
  }, [responseUserId, responseCompanyId]);

  // Fetch usage when company or month changes (only if authenticated)
  useEffect(() => {
    if (isAuthenticated && selectedCompany) {
      fetchUsage();
    }
  }, [selectedCompany, selectedMonth, responseUserId]);

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (responseUserId) params.set('userId', responseUserId);
      if (responseCompanyId) params.set('currentCompanyId', responseCompanyId);
      
      const res = await fetch(`${API_BASE}/api/usage/companies?${params}`);
      const data = await res.json();
      if (data.success) {
        setAllCompanies(data.companies || []);
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const fetchUsage = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        companyId: selectedCompany,
        month: selectedMonth,
      });
      if (responseUserId) params.set('userId', responseUserId);

      const res = await fetch(`${API_BASE}/api/usage?${params}`);
      const data = await res.json();
      if (data.success) {
        setUsageData(data.usage);
      } else {
        setError(data.error || 'Failed to fetch usage data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedMonth, responseUserId]);

  const usagePct = usageData ? (usageData.monthlyTotal / usageData.limit) * 100 : 0;
  
  // Color logic for the gauge
  let gaugeColor = 'bg-cyan-500';
  let gaugeTextColor = 'text-cyan-400';
  let gaugeBorderColor = 'border-cyan-500/30';
  if (usagePct >= 90) {
    gaugeColor = 'bg-red-500';
    gaugeTextColor = 'text-red-400';
    gaugeBorderColor = 'border-red-500/30';
  } else if (usagePct >= 75) {
    gaugeColor = 'bg-yellow-500';
    gaugeTextColor = 'text-yellow-400';
    gaugeBorderColor = 'border-yellow-500/30';
  }

  return (
    <div className="space-y-4">
      <MonitoredCompaniesSection />
      {/* Header */}
      <div className="bg-gray-800 p-3 rounded border border-cyan-500/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-cyan-400">
            <GaugeIcon />
            <span className="text-xs font-bold uppercase">API Usage Tracker</span>
          </div>
          {isAuthenticated && (
            <button 
              onClick={() => { fetchCompanies(); fetchUsage(); }}
              disabled={loading}
              className="p-1.5 text-gray-400 hover:text-cyan-400 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshIcon />
            </button>
          )}
        </div>

        {/* User info badge */}
        {isAuthenticated && (
          <div className="mb-2 px-2 py-1 bg-gray-700 rounded text-[10px] text-gray-400">
            Tracking as: <span className="text-cyan-400 font-mono">{responseUserId}</span>
            {isExtUser && <span className="ml-2 text-yellow-400">(cross-company access)</span>}
          </div>
        )}

        {/* Not authenticated state */}
        {!isAuthenticated && (
          <div className="text-center text-gray-500 text-xs py-4">
            <span className="text-gray-400 font-medium">Not connected</span>
            <br /><br />
            <span className="text-gray-600">
              Make an API call from any tab to start tracking usage.
              Your company and user will be detected automatically.
            </span>
          </div>
        )}

        {/* Company & Month Selectors - only show when authenticated */}
        {isAuthenticated && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Company</label>
              {isExtUser ? (
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-cyan-500"
                >
                  <option value="">Select company...</option>
                  {allCompanies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {responseCompanyId && !allCompanies.includes(responseCompanyId) && (
                    <option value={responseCompanyId}>{responseCompanyId} (current)</option>
                  )}
                </select>
              ) : (
                <div className="w-full bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded px-2 py-1.5">
                  {selectedCompany || responseCompanyId || 'Detecting...'}
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Month</label>
              <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs p-2 rounded">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center text-gray-400 text-xs py-4">Loading usage data...</div>
      )}

      {!loading && usageData && (
        <>
          {/* Big Gauge */}
          <div className={`bg-gray-800 p-4 rounded border ${gaugeBorderColor}`}>
            <div className="text-center mb-3">
              <div className={`text-3xl font-bold ${gaugeTextColor} font-mono`}>
                {formatNumber(usageData.monthlyTotal)}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                of {formatNumber(usageData.limit)} monthly API call limit
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
              <div 
                className={`${gaugeColor} h-full rounded-full transition-all duration-500 relative`}
                style={{ width: `${Math.min(usagePct, 100)}%` }}
              >
                {usagePct >= 15 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-900">
                    {usagePct.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            
            {usagePct < 15 && (
              <div className="text-center mt-1">
                <span className={`text-[10px] font-bold ${gaugeTextColor}`}>
                  {usagePct.toFixed(1)}%
                </span>
              </div>
            )}

            {/* Warning messages */}
            {usagePct >= 90 && (
              <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded p-2 text-center">
                <span className="text-xs text-red-400 font-bold">⚠ CRITICAL: Approaching 100,000 call limit!</span>
              </div>
            )}
            {usagePct >= 75 && usagePct < 90 && (
              <div className="mt-2 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-center">
                <span className="text-xs text-yellow-400 font-bold">⚡ Heads up: 75% of monthly limit used</span>
              </div>
            )}

            {/* Remaining calls */}
            <div className="mt-2 text-center text-xs text-gray-500">
              {formatNumber(Math.max(usageData.limit - usageData.monthlyTotal, 0))} calls remaining this month
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex gap-1 bg-gray-800 p-1 rounded">
            {[
              { id: 'daily', label: 'Daily' },
              { id: 'functions', label: 'By Function' },
              { id: 'history', label: 'History' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`flex-1 text-xs py-1.5 rounded font-medium transition-colors ${
                  activeView === tab.id 
                    ? 'bg-gray-700 text-cyan-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Daily Breakdown */}
          {activeView === 'daily' && (
            <div className="bg-gray-800 p-3 rounded border border-gray-700">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">
                Daily Calls — {selectedMonth}
              </h3>
              <BarChart 
                data={usageData.dailyBreakdown} 
                labelKey="date" 
                valueKey="total_calls"
                color="cyan"
              />
            </div>
          )}

          {/* Function Breakdown */}
          {activeView === 'functions' && (
            <div className="bg-gray-800 p-3 rounded border border-gray-700">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">
                Calls by Function — {selectedMonth}
              </h3>
              <BarChart 
                data={usageData.functionBreakdown} 
                labelKey="function_name" 
                valueKey="total_calls"
                color="yellow"
              />
            </div>
          )}

          {/* Monthly History */}
          {activeView === 'history' && (
            <div className="bg-gray-800 p-3 rounded border border-gray-700">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">
                Monthly History
              </h3>
              <BarChart 
                data={usageData.monthlySummary} 
                maxValue={100000}
                labelKey="month" 
                valueKey="total_calls"
                color="emerald"
              />
              {usageData.monthlySummary && usageData.monthlySummary.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <table className="w-full text-[10px] text-gray-400">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left py-0.5">Month</th>
                        <th className="text-right py-0.5">Calls</th>
                        <th className="text-right py-0.5">% of Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.monthlySummary.map((m, i) => (
                        <tr key={i} className="hover:text-gray-300 cursor-pointer" onClick={() => setSelectedMonth(m.month)}>
                          <td className="py-0.5 font-mono">{m.month}</td>
                          <td className="py-0.5 text-right font-mono">{formatNumber(m.total_calls)}</td>
                          <td className={`py-0.5 text-right font-mono ${
                            (m.total_calls / 100000) >= 0.9 ? 'text-red-400' :
                            (m.total_calls / 100000) >= 0.75 ? 'text-yellow-400' : ''
                          }`}>
                            {((m.total_calls / 100000) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {isAuthenticated && !loading && !usageData && selectedCompany && (
        <div className="text-center text-gray-500 text-xs py-4">
          No usage data found for {selectedCompany}
        </div>
      )}
    </div>
  );
};

export default ApiUsagePanel;